import { motion } from 'framer-motion'
import { Kicker } from './Kicker'

/**
 * Growth velocity — kg/week delta computed from consecutive measurements.
 * Handy for pediatric visits ("gained 0.19 kg this week, on track").
 * WHO expected gain 0-3mo: ~0.15-0.30 kg/week. 3-6mo: ~0.10-0.15. 6-12mo: ~0.07-0.10.
 */

export interface WeightPoint {
  at: string        // ISO
  kg: number
  ageMonths: number
}

interface Props {
  points: WeightPoint[]
}

export function GrowthVelocity({ points }: Props) {
  if (points.length < 2) return null

  // Compute deltas per adjacent pair, kg/week
  const deltas = points.slice(1).map((p, i) => {
    const prev = points[i]
    const daysBetween = (new Date(p.at).getTime() - new Date(prev.at).getTime()) / 86_400_000
    if (daysBetween <= 0) return null
    const kgPerWeek = ((p.kg - prev.kg) / daysBetween) * 7
    return { at: p.at, kgPerWeek, ageMonths: p.ageMonths, daysBetween }
  }).filter((x): x is NonNullable<typeof x> => x != null)

  if (deltas.length === 0) return null

  const latest = deltas[deltas.length - 1]
  const expected = expectedGainKgPerWeek(latest.ageMonths)
  const status = classify(latest.kgPerWeek, expected)

  return (
    <div className="glass-tile p-4">
      <Kicker>Growth velocity</Kicker>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="numeric-hero" style={{ fontSize: 'clamp(32px, 8vw, 44px)' }}>
          {latest.kgPerWeek >= 0 ? '+' : ''}{latest.kgPerWeek.toFixed(2)}
        </span>
        <span className="text-body text-text-tertiary">kg/week</span>
      </div>
      <div className="mt-1 text-caption text-text-secondary">
        Since last measurement ({Math.round(latest.daysBetween)} days)
      </div>

      <div className="mt-3 rounded-r-sm p-3 text-caption" style={{ background: statusBg(status), color: statusFg(status) }}>
        {statusLabel(status)}
        <span className="text-text-tertiary ml-2">
          — expected {expected.min.toFixed(2)}-{expected.max.toFixed(2)} kg/week at {latest.ageMonths.toFixed(0)}mo
        </span>
      </div>

      {deltas.length > 1 && (
        <div className="mt-4">
          <div className="text-tag text-text-tertiary">Recent trend</div>
          <div className="mt-2 flex items-end gap-1 h-12">
            {deltas.slice(-8).map((d, i) => {
              const max = Math.max(...deltas.slice(-8).map((x) => Math.abs(x.kgPerWeek)))
              const h = (Math.abs(d.kgPerWeek) / (max || 1)) * 100
              const above = d.kgPerWeek >= 0
              return (
                <motion.div
                  key={i}
                  initial={{ height: 0 }} animate={{ height: `${h}%` }}
                  transition={{ duration: 0.3, delay: i * 0.03 }}
                  className="flex-1 rounded-t"
                  style={{ background: above ? '#2ED2A3' : '#E85E6E', opacity: 0.75 }}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function expectedGainKgPerWeek(ageMonths: number): { min: number; max: number } {
  if (ageMonths < 3)  return { min: 0.15, max: 0.30 }
  if (ageMonths < 6)  return { min: 0.10, max: 0.15 }
  if (ageMonths < 12) return { min: 0.07, max: 0.10 }
  if (ageMonths < 24) return { min: 0.04, max: 0.07 }
  return { min: 0.03, max: 0.05 }
}

type Status = 'below' | 'ok' | 'above' | 'losing'

function classify(kgPerWeek: number, expected: { min: number; max: number }): Status {
  if (kgPerWeek < 0) return 'losing'
  if (kgPerWeek < expected.min) return 'below'
  if (kgPerWeek > expected.max * 1.5) return 'above'
  return 'ok'
}

function statusLabel(s: Status): string {
  switch (s) {
    case 'ok':     return 'On track'
    case 'below':  return 'Below expected'
    case 'above':  return 'Above expected'
    case 'losing': return 'Losing weight — talk to your pediatrician'
  }
}
function statusBg(s: Status): string {
  switch (s) {
    case 'ok':     return 'rgba(46,210,163,0.12)'
    case 'below':  return 'rgba(255,184,77,0.12)'
    case 'above':  return 'rgba(46,210,163,0.10)'
    case 'losing': return 'rgba(232,94,110,0.14)'
  }
}
function statusFg(s: Status): string {
  switch (s) {
    case 'ok':     return '#1FA47F'
    case 'below':  return '#B8860B'
    case 'above':  return '#1FA47F'
    case 'losing': return '#D14758'
  }
}
